import { useEffect, useState } from 'react';
import { callableErrorMessage, guideAvailability } from '@/util/callable_functions';
import { toCalendarDate, todayCalendarDate } from '@/util/util_format';

/**
 * The dates one guide is free, `yyyy-mm-dd`.
 *
 * A set of free days rather than a map of every day and its state: /portal
 * answers in whole days, so a date is in here or it is not, and the window
 * bounds say which dates were asked about at all.
 */
export type GuideDiaryType = Set<string>;

/** Shared empty result, so "nobody to ask about" is a stable object identity. */
const noDiaries: Map<string, GuideDiaryType> = new Map();

/**
 * Whether to ask /portal for diaries at all.
 *
 * Off while the calendar sync is still being settled at /portal's end. Nothing
 * below is deleted — the callable, the wire types and their
 * `internal_guideAvailability` endpoint all still exist and still typecheck — so
 * turning this back to `true` is the whole of switching it on.
 *
 * With it off the page says so rather than guessing: every caller shows the
 * "coming soon" state and points the concierge at the desk, instead of drawing
 * an empty diary that would read as "this tour leader is never free".
 *
 * Typed as `boolean` rather than left to infer `false`, so the branches guarded
 * by it are not narrowed away as dead code while it is off.
 */
export const liveGuideAvailability: boolean = false;

interface FetchedType {
  /** The `key` this result answers. Anything else means it is stale. */
  key: string;
  diaries: Map<string, GuideDiaryType>;
  error: string | null;
}

/**
 * The diaries of every named guide, keyed by `portalGuideId`.
 *
 * One call for the whole roster rather than one per guide, and the same result
 * serves both the grid and the profile dialog. That is what makes the "available
 * today" dot on each card affordable: the page needs today's state for everyone
 * before anybody has clicked anything, so the alternative is a request per card
 * on load.
 *
 * The window is three months from today, fixed for the life of the hook, so
 * paging months inside a profile never refetches and the range cannot shift
 * under a concierge who leaves the page open past midnight. Beyond it the
 * calendar simply shows nothing, which is honest — a guide's diary that far out
 * is not settled.
 *
 * Guides with a blank `portalGuideId` are not asked about, and guides /portal
 * does not recognise come back absent; neither is an error, and callers show
 * "availability on request" for them.
 *
 * While `liveGuideAvailability` is off nothing is fetched at all: no diaries, no
 * error, and never loading — callers check the flag and show the coming-soon
 * state instead of an answer.
 */
export function useGuidesAvailability(portalGuideIds: string[]) {
  const [fetched, setFetched] = useState<FetchedType | null>(null);
  const [{ fromDate, toDate }] = useState(availabilityWindow);

  // Keyed on the joined ids rather than the array: callers derive that array
  // inside a render, and a new array of the same ids must not refetch.
  const key = [...portalGuideIds].filter(Boolean).sort().join(',');

  useEffect(() => {
    if (!liveGuideAvailability || !key) return;

    let cancelled = false;

    guideAvailability({ portalGuideIds: key.split(','), fromDate, toDate })
      .then((response) => {
        if (cancelled) return;
        const diaries = new Map<string, GuideDiaryType>();
        for (const [portalGuideId, dates] of Object.entries(response.data.guides)) {
          diaries.set(portalGuideId, new Set(dates));
        }
        setFetched({ key, diaries, error: null });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.error('guideAvailability failed', e);
        // The calendar is one part of a page that stands without it — profiles
        // come from our own Firestore — so this is reported in place of the grid
        // rather than replacing the page.
        setFetched({ key, diaries: noDiaries, error: callableErrorMessage(e) });
      });

    return () => {
      cancelled = true;
    };
  }, [key, fromDate, toDate]);

  // Derived rather than kept in state, so nothing has to be reset when the
  // roster changes: a result for a different key is simply not this one's, and
  // "loading" is exactly the window in which we hold no answer for it.
  const current = fetched?.key === key ? fetched : null;

  return {
    diaries: current?.diaries ?? noDiaries,
    error: current?.error ?? null,
    loading: liveGuideAvailability && !!key && !current,
    fromDate,
    toDate,
  };
}

/** Today to three months out — within `guideAvailabilityMaxDays`. */
function availabilityWindow() {
  const fromDate = todayCalendarDate();
  const end = new Date();
  end.setMonth(end.getMonth() + 3);
  return { fromDate, toDate: toCalendarDate(end) };
}
